import { useTranslation } from '@utils/i18n/i18n';

function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatNumber(num, decimals = 1) {
  const rounded = Math.round(num * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(decimals);
}

function getFormFactor(feature) {
  if (!feature?.properties?.vehicleType) {
    return undefined;
  }

  if (feature.properties.vehicleType.form_factor) {
    return feature.properties.vehicleType.form_factor;
  }

  try {
    const parsedVehicleType = JSON.parse(feature.properties.vehicleType);
    return parsedVehicleType.form_factor;
  } catch (err) {
    return undefined;
  }
}

function getStationVehicleTypes(feature, properties) {
  const stationVehicleTypes = properties?.vehicles?.map(v => ({
    ...v?.vehicleType,
    availability: v.availability,
    capacity: v.capacity,
  })) || [];
  const isCargoBikeStation = properties?.acceptable_bike_types?.includes('cargobike');

  if (isCargoBikeStation && feature?.properties?.marker_counter === 0) {
    return [{ form_factor: 'cargo_bicycle', availability: 0 }];
  }

  if (stationVehicleTypes.length > 0) return stationVehicleTypes;
  if (isCargoBikeStation) return [{ form_factor: 'cargo_bicycle', availability: 0 }];
  return [properties?.vehicleType];
}

const useFormFactorText = () => {
  const { t } = useTranslation();

  return {
    car: { single: t('popup_transport-type_car_single'), multi: t('popup_transport-type_car_multi') },
    bicycle: { single: t('popup_transport-type_bicycle_single'), multi: t('popup_transport-type_bicycle_multi') },
    cargo_bicycle: { single: t('popup_transport-type_cargo_bicycle_single'), multi: t('popup_transport-type_cargo_bicycle_multi') },
    scooter: { single: t('popup_transport-type_scooter_single'), multi: 'popup_transport-type_scooter_multi' },
    moped: { single: t('popup_transport-type_moped_single'), multi: 'popup_transport-type_moped_multi' },
  }
}

const useReturnContraintsText = () => {
  const { t } = useTranslation();

  return {
    car: {
      roundtrip_station: t('popup_return-option_roundtrip-station-car'),
      roundtrip_homezone: t('popup_return-option_roundtrip-homezone-car'), 
      any_station: t('popup_return-option_any-station-car'),
      free_floating: t('popup_return-option_free-floating-car'),
    },
    moped: {
      roundtrip_station: t('popup_return-option_roundtrip-station-moped'),
      any_station: t('popup_return-option_any-station-moped'),
      free_floating: t('popup_return-option_free-floating-moped'),
    },
    bicycle: {
      roundtrip_station: t('popup_return-option_roundtrip-station-bicycle'),
      any_station: {
        station: t('popup_return-option_any-station-bicycle-station'),
        free: t('popup_return-option_any-station-bicycle-free'),
      },
      free_floating: t('popup_return-option_free-floating-bicycle'),
    },
    cargo_bicycle: {
      roundtrip_station: t('popup_return-option_roundtrip-station-cargo'),
      any_station: t('popup_return-option_any-station-cargo'),
      free_floating: t('popup_return-option_free-floating-cargo'),
    },
    scooter: {
      roundtrip_station: t('popup_return-option_roundtrip-station-scooter'),
      any_station: t('popup_return-option_any-station-scooter'),
      free_floating: t('popup_return-option_free-floating-scooter'),
    },
  }
}

function minutesToNiceString(min, t) {
  const weeks = Number(Math.floor(min / (60 * 24 * 7)));
  const remainingMinutesAfterWeeks = Number(min % (60 * 24 * 7));

  const days = Number(Math.floor(remainingMinutesAfterWeeks / (60 * 24)));
  const remainingMinutesAfterDays = Number(remainingMinutesAfterWeeks % (60 * 24));

  const hours = Number(Math.floor(remainingMinutesAfterDays / 60));
  const minutes = Number(remainingMinutesAfterDays % 60);

  return (
    `${weeks ? `${formatNumber(weeks)}${weeks === 1 ? ` ${t('popup_pricing-plan_week')}` : ` ${t('popup_pricing-plan_weeks')}`}` : ''}`
    + `${weeks && days ? ' ' : ''}`
    + `${days ? `${formatNumber(days)}${days === 1 ? ` ${t('popup_pricing-plan_day')}` : ` ${t('popup_pricing-plan_days')}`}` : ''}`
    + `${days && hours ? ' ' : ''}`
    + `${hours ? `${formatNumber(hours)}${hours === 1 ? ` ${t('popup_pricing-plan_hour')}` : ` ${t('popup_pricing-plan_hours')}`}` : ''}`
    + `${hours && minutes ? ' ' : ''}`
    + `${minutes ? `${formatNumber(minutes)}${minutes === 1 ? ` ${t('popup_pricing-plan_minute')}` : ` ${t('popup_pricing-plan_minutes')}`}` : ''}`
  )
}

export {
  capitalize, formatNumber, getFormFactor, getStationVehicleTypes, useFormFactorText,
  useReturnContraintsText,
  minutesToNiceString,
};
